import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { MessageService } from 'primeng/api';
import { BehaviorSubject, combineLatest, forkJoin, Observable, Subject, Subscription } from 'rxjs';
import { finalize, first, takeUntil, tap } from 'rxjs/operators';
import { User } from 'src/app/shared/classes/user';
import { RegionRole } from 'src/app/shared/models/user';
import { AuthenticationService } from 'src/app/shared/services/authentication.service';
import { ModalService } from 'src/app/shared/services/modal.service';
import { PicalunaSiteService } from 'src/app/shared/services/picaluna-site.service';
import { SiteService } from 'src/app/shared/services/site.service';
import { UserService } from 'src/app/shared/services/user.service';
import { PasswordMatcher } from 'src/app/shared/validators/confirmPassword';
import { minLengthArray } from 'src/app/shared/validators/minArrayLength';

@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.scss'],
})
export class RegisterComponent implements OnInit, OnDestroy {
  private unsubscribe$ = new Subject<void>();

  @Input() isModal: boolean = false;

  registerForm = this.formBuild.group(
    {
      email: ['', [Validators.required, Validators.email]],
      password: ['', Validators.required],
      passwordConfirm: ['', Validators.required],
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      phone: [''],
      roles: [[], minLengthArray(1)],
      regions: [[], minLengthArray(1)],
      avatar: [''],
      serviceProviderId: [''],
      emailSignature: [''],
      // Admin-controlled: publish this arranger on the picaluna.com.au site.
      // The profile content itself (job title, bio, credentials, languages) is
      // edited by the arranger on their own profile/settings page.
      isPublicProfile: [false],
    },
    {
      validators: [PasswordMatcher.match('password', 'passwordConfirm')],
    }
  );

  roles: RegionRole[] = [];
  regions: RegionRole[] = [];
  subscriptions: Subscription[] = [];
  userId: string | null;
  user$: Observable<User> = this.userService.user$;
  roles$: Observable<RegionRole[]> = this.userService.roles$;
  regions$: Observable<RegionRole[]> = this.userService.regions$;

  currentUserSource: BehaviorSubject<User | null>;
  currentUser$!: Observable<User | null>;

  isProcessingUser: boolean;
  isProcessingUserEnabled: boolean;
  isProcessingResetPassword: boolean;

  // ── Staging preview of the director page (SITE-PUBLISHING pipeline) ──────
  /** Staging Worker base URL; null = pipeline unavailable → preview UI hidden. */
  stagingBaseUrl: string | null = null;
  previewRequesting = false;
  previewBuilding = false;
  private previewDispatchedAt = 0;

  constructor(
    private siteService: SiteService,
    private formBuild: FormBuilder,
    private userService: UserService,
    private authenticationService: AuthenticationService,
    private messageService: MessageService,
    private modalService: ModalService,
    private activatedRoute: ActivatedRoute,
    private picalunaSiteService: PicalunaSiteService
  ) {
    this.isProcessingUser = false;
    this.isProcessingUserEnabled = false;
    this.isProcessingResetPassword = false;

    this.userId = this.activatedRoute.snapshot.paramMap.get('id');

    this.currentUserSource = new BehaviorSubject<User | null>(null);
    this.currentUser$ = this.currentUserSource.asObservable();

    if (this.userId) {
      this.registerForm.controls.password.setValidators(null);
      this.registerForm.controls.passwordConfirm.setValidators(null);

      this.registerForm.controls.password.updateValueAndValidity();
      this.registerForm.controls.passwordConfirm.updateValueAndValidity();

      this.userService.allUsers$.pipe(takeUntil(this.unsubscribe$)).subscribe((users: User[]) => {
        const user = users.find((user: User) => user.data.id.toString() === this.userId);

        if (user) {
          this.currentUserSource.next(user);

          this.registerForm.patchValue(user.data);
          // isPublicProfile comes back as 1/0 from the DB; p-inputSwitch only
          // shows "on" for a strict boolean true, so coerce it.
          this.registerForm.get('isPublicProfile')?.setValue(!!user.data.isPublicProfile);
        }
      });
    } else {
      this.currentUserSource.next(this.userService.userSourceValue);
    }
  }

  ngOnInit(): void {
    if (this.isModal) {
      this.userService.user$.pipe(takeUntil(this.unsubscribe$)).subscribe((user) => {
        this.userId = user.data.id.toString();
        this.registerForm.patchValue(user.data as any);
        this.registerForm.get('isPublicProfile')?.setValue(!!user.data.isPublicProfile);
      });

      ['roles', 'regions'].map((item) => {
        this.registerForm.get(item)?.clearValidators();
        this.registerForm.get(item)?.updateValueAndValidity();
      });
    }

    // Staging preview availability — best-effort; only relevant when editing an
    // existing user (the Director Profile block is userId-gated too).
    if (this.userId) {
      this.picalunaSiteService
        .status()
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe({
          next: (status) => {
            this.stagingBaseUrl = (status.stagingUrl || '').replace(/\/+$/, '') || null;
          },
          error: () => {
            this.stagingBaseUrl = null;
          },
        });
    }
  }

  /**
   * The director page URL for this user on the staging Worker. Mirrors the
   * sync's slug (slugify "first last"); collision-suffixed slugs (rare
   * duplicate names) may differ — the directory index on staging always works.
   */
  get stagingProfileUrl(): string {
    if (!this.stagingBaseUrl) {
      return '';
    }

    const first = String(this.registerForm.get('firstName')?.value || '').trim();
    const last = String(this.registerForm.get('lastName')?.value || '').trim();
    const slug = `${first} ${last}`
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

    return slug ? `${this.stagingBaseUrl}/funeral-directors/${slug}/` : '';
  }

  /**
   * Rebuild the staging site so this profile (published or not — the preview
   * projection includes not-yet-public profiles) can be reviewed on the
   * noindexed staging Worker. Builds from SAVED data.
   */
  onPreviewProfileOnStaging(): void {
    if (this.registerForm.dirty) {
      this.modalService.generic({
        title: 'Save First',
        copy: [
          'The staging preview builds from saved data, and this form has unsaved changes.',
          'Submit the form, then click "Preview on staging" again.',
        ],
        buttons: [{ label: 'OK', key: 'ok', class: '' }],
      });
      return;
    }

    this.previewRequesting = true;

    // Scope to arranger profiles rather than re-syncing the whole site (see the
    // RSVP panel for why). `blogs` rides along because blog endorsements
    // denormalise the director slug — same reasoning as the server-side profile
    // trigger in api.user.ts.
    this.picalunaSiteService
      .publish('directors blogs')
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: () => {
          this.previewRequesting = false;
          this.previewBuilding = true;
          this.previewDispatchedAt = Date.now();

          this.picalunaSiteService
            .pollSyncCompletion(this.previewDispatchedAt)
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
              next: (run) => {
                this.messageService.add(
                  run.conclusion === 'success'
                    ? {
                        severity: 'success',
                        summary: 'Staging preview ready',
                        detail: 'Open "View on staging" to review the director page.',
                      }
                    : {
                        severity: 'error',
                        summary: 'Staging preview failed',
                        detail:
                          'The staging build did not complete — check the Accounts page for the build log.',
                      }
                );
              },
              complete: () => {
                this.previewBuilding = false;
              },
            });
        },
        error: (err) => {
          this.previewRequesting = false;
          this.modalService.error(err?.error?.error || err);
        },
      });
  }

  onToggleUserIsEnabled(event: Event, user: User): void {
    const processRequest = () => {
      this.isProcessingUserEnabled = true;

      user.toggleEnableStatus().subscribe({
        next: () => {
          this.modalService.generic({
            title: 'Status Updated',
            copy: ['You successfully updated the status of the user.'],
            buttons: [{ label: 'Close', key: 'close', class: '' }],
          });

          this.isProcessingUserEnabled = false;
        },
        error: (err) => {
          this.modalService.error(err.error.error);
          this.isProcessingUserEnabled = false;
        },
      });
    };

    const modal = this.modalService.generic({
      title: 'Confirm Request',
      copy: ["Are you sure you want to change the user's status?"],
      buttons: [
        { label: 'Cancel', key: 'cancel', class: 'p-button-secondary' },
        { label: 'Confirm', key: 'confirm', class: '' },
      ],
    });

    modal.onClose.pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (key) => {
        if (key === 'confirm') {
          processRequest();
        }
      },
    });
  }

  onResetPassword(event: Event, user: User): void {
    const processRequest = () => {
      this.isProcessingResetPassword = true;

      this.authenticationService.forgotPassword(user.data.email).subscribe({
        next: (res) => {
          this.modalService.generic({
            title: 'Reset Successful',
            copy: ["Instructions to change the user's password have successfully been sent."],
            buttons: [{ label: 'Close', key: 'close', class: '' }],
          });

          this.isProcessingResetPassword = false;
        },
        error: (err) => {
          this.modalService.error(err.error.error);
          this.isProcessingResetPassword = false;
        },
      });
    };

    const modal = this.modalService.generic({
      title: 'Confirm Request',
      copy: ["Are you sure you want to reset the user's password?"],
      buttons: [
        { label: 'Cancel', key: 'cancel', class: 'p-button-secondary' },
        { label: 'Confirm', key: 'confirm', class: '' },
      ],
    });

    modal.onClose.pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (key) => {
        if (key === 'confirm') {
          processRequest();
        }
      },
    });
  }

  onFormSubmit(event: Event) {
    if (this.userId) {
      this.updateUser();
    } else {
      this.registerUser();
    }
  }

  generatePassword(): void {
    const value =
      'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPWRSTUVWXYZ0123456789!@#$%^&*-_=+\\|:;.\<>/?~';

    let newPassword = '';

    for (var i = 0; i < 12; i++) {
      newPassword += value[Math.floor(Math.random() * value.length)];
    }

    this.registerForm.controls.password.setValue(newPassword);
    this.registerForm.controls.passwordConfirm.setValue(newPassword);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => {
      if (subscription) {
        subscription.unsubscribe();
      }
    });

    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private registerUser(): void {
    this.isProcessingUser = true;

    const rxjsOptions = {
      next: (res: any) => {
        this.modalService.generic({
          title: 'User Creation Successful',
          copy: [`You successfully created a new account.`],
          buttons: [{ label: 'Close', key: 'close', class: '' }],
        });

        if (!this.userId) {
          this.registerForm.reset();
        }

        this.isProcessingUser = false;
      },
      error: (err: any) => {
        this.modalService.error(err.error.error);

        this.isProcessingUser = false;
      },
    };

    this.authenticationService.register(this.registerForm.value).subscribe(rxjsOptions);
  }

  private updateUser(): void {
    const processRequest = () => {
      this.isProcessingUser = true;

      const rxjsOptions = {
        next: (res: any) => {
          if (this.isModal) {
            window.location.reload();
          }
          this.modalService.generic({
            title: 'Update Successful',
            copy: [`You've successfully updated the account.`],
            buttons: [{ label: 'Close', key: 'close', class: '' }],
          });

          this.isProcessingUser = false;
        },
        error: (err: any) => {
          this.modalService.error(err.error.error);

          this.isProcessingUser = false;
        },
      };

      this.siteService.addSubscriptionLog(
        this,
        'register.component.ts->updateUser->processRequest->this.currentUser$'
      );

      this.currentUser$
        .pipe(
          finalize(() =>
            this.siteService.setSubscriptionLogFinalised(
              'register.component.ts->updateUser->processRequest->this.currentUser$'
            )
          ),
          takeUntil(this.unsubscribe$),
          first()
        )
        .subscribe({
          next: (user) => {
            if (user) {
              const data = this.registerForm.value;

              user.update(data).subscribe(rxjsOptions);
            }
          },
        });
    };

    const modal = this.modalService.generic({
      title: 'Confirm Request',
      copy: ["Are you sure you want to modify the user's details?"],
      buttons: [
        { label: 'Cancel', key: 'cancel', class: 'p-button-secondary' },
        { label: 'Confirm', key: 'confirm', class: '' },
      ],
    });

    modal.onClose.pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (key) => {
        if (key === 'confirm') {
          processRequest();
        }
      },
    });
  }
}
